import { createContext, useContext, useState, useEffect } from 'react';

const translations = {
  en: {
    // Navigation
    nav: {
      home: "Home",
      about: "About Us",
      contact: "Contact",
      login: "Login",
      register: "Register",
      dashboard: "Dashboard",
      consultation: "Book Consultation",
      faq: "FAQ",
      // CAPMARKETS-236 — the verify dropdown's own label. Was the literal
      // "Get Started", which collided with the register CTA in the same bar.
      verification: "Verification"
    },
    
    // Landing Page
    landing: {
      hero_title: "Where High Net Worth Professionals Connect and Grow Relationships",
      hero_subtitle: "Welcome to the private membership portal of EchoSense Capital Club—where relationships create access and access creates opportunity. Complete your profile and verification to enter a private community built around collaboration, education, curated partners, and opportunities typically reserved for institutions.",
      cta_primary: "Get Started",
      cta_secondary: "About the Capital Club",
      invite_badge: "Exclusive Network Access"
    },
    
    // About Page - Compliance Focused
    about: {
      title: "About EchoSense Capital Club",
      subtitle: "Introducer Management & Compliance Tracking Platform",
      
      section1_title: "What is EchoSense Capital Club?",
      section1_content: "EchoSense Capital Club operates as an introducer management and compliance tracking platform, distinct from EchoSense Capital Club's investment operations. It's built for tracking referrals, onboarding progress, and points — not for selling anything or giving investment advice.",
      
      section2_title: "What EchoSense Capital Club Does NOT DO!",
      section2_items: [
        "Offer investments",
        "Recommend securities",
        "Accept capital",
        "Provide investment advice"
      ],
      section2_note: "Introducers do not discuss terms, returns, or structures.",
      
      section3_title: "Why This Structure?",
      section3_content: "Activity is limited to introductions and status visibility. No transaction-based compensation tied to securities sales. No discretion, persuasion, or solicitation. Points system tracks administrative actions, not capital placement.",
      
      section4_title: "KYC & Compliance",
      section4_content: "Introducer-level KYC is performed preemptively to prevent post-funding compliance delays, satisfy AML and payment controls. KYC is separate from investor onboarding.",
      
      section5_title: "Structural Separation",
      section5_content: "EchoSense Capital Club and EchoSense Capital Club are separate entities. EchoSense Capital Club does not intermediate transactions. EchoSense Capital Club independently handles all investor suitability, onboarding, and funding.",
      
      conclusion_title: "Compliance Statement",
      conclusion_content: "EchoSense Capital Club functions as a non-transactional referral management system, not a broker, dealer, or finder under U.S. securities laws."
    },
    
    // FAQ Page
    faq: {
      title: "Frequently Asked Questions",
      subtitle: "Everything you need to know about EchoSense Capital Club",
      
      questions: [
        {
          q: "What is EchoSense Capital Club?",
          a: "EchoSense Capital Club is the backend portal for tracking referrals connected to EchoSense Capital Club. No investing, no pitching, no selling. Just a clean way to see who you've introduced, where they are in onboarding/funding, and handle compliance early so nothing gets stuck later."
        },
        {
          q: "Is EchoSense Capital Club an investment platform?",
          a: "No. EchoSense Capital Club is strictly for introducer tracking and compliance. It does not offer investments, recommend securities, accept capital, or provide investment advice. It operates completely separate from EchoSense Capital Club's investment operations."
        },
        {
          q: "What can I see in my dashboard?",
          a: "You can see: Who you introduced, where they are in onboarding or funding, who they introduced. No balances. No investment details. Just status and progress."
        },
        {
          q: "Why do I need to complete KYC?",
          a: "This helps avoid delays later as your referral activity progresses. Doing this now keeps things smooth if funds move later. KYC is performed preemptively to satisfy AML and payment controls."
        },
        {
          q: "How does the points system work?",
          a: "Points update automatically as: You complete setup steps, your referrals move through onboarding, second-level introductions progress. Points are for tracking and transparency, not performance."
        },
        {
          q: "Is this broker-dealer activity?",
          a: "No. Activity is limited to introductions and status visibility. There's no transaction-based compensation tied to securities sales, no discretion, persuasion, or solicitation. The points system tracks administrative actions, not capital placement."
        },
        {
          q: "What's the relationship between EchoSense Capital Club and EchoSenseAi / FinBPM?",
          a: "They are separate entities with separate ownership and jurisdictional framework."
        },
        {
          q: "Can I discuss investment terms with my referrals?",
          a: "No. Introducers do not discuss terms, returns, or structures. EchoSense Capital Club keeps everything clean so you never look like you're selling or promoting a security."
        }
      ]
    },
    
    // Onboarding Steps
    onboarding: {
      step1_title: "Create Your EchoSense Capital Club Account",
      step1_desc: "Set up your login so we know who you are in the system. This is for tracking and compliance only — not investing.",
      
      step2_title: "Complete Introducer KYC (Early)",
      step2_desc: "This helps avoid delays later as your referral activity progresses. Doing this now keeps things smooth if funds move later.",
      
      step3_title: "View Your Referral Dashboard",
      step3_desc: "See who you introduced, where they are in onboarding or funding, and who they introduced. No balances. No investment details. Just status and progress.",
      
      step4_title: "Points Track Your Activity",
      step4_desc: "Points update automatically as you complete setup steps, your referrals move through onboarding, and second-level introductions progress. Points are for tracking and transparency, not performance.",
      
      step5_title: "Stay Compliant, Stay Visible",
      step5_desc: "EchoSense Capital Club keeps everything clean so you never look like you're selling or promoting a security."
    },
    
    // Invite Messages
    invite: {
      short: "You're invited to EchoSense Capital Club — the backend portal for tracking referrals connected to EchoSense Capital Club. No investing, no pitching, no selling. Just a clean way to see who you've introduced.",
      long: "You've been invited to EchoSense Capital Club — the introducer portal that sits next to EchoSense Capital Club (not inside it). It's built for tracking referrals, onboarding progress, and points — not for selling anything or giving investment advice. Set up your account, knock out KYC early, and get visibility into your network without headaches later."
    },
    
    // Common
    common: {
      activate_here: "Activate here",
      learn_more: "Learn More",
      get_started: "Get Started",
      contact_us: "Contact Us",
      back: "Back",
      next: "Next",
      submit: "Submit",
      cancel: "Cancel",
      save: "Save",
      loading: "Loading...",
      error: "Error",
      success: "Success"
    }
  },
  
  es: {
    // Navigation
    nav: {
      home: "Inicio",
      about: "Sobre Nosotros",
      contact: "Contacto",
      login: "Iniciar Sesión",
      register: "Registrarse",
      dashboard: "Panel",
      consultation: "Reservar Consulta",
      faq: "Preguntas Frecuentes",
      verification: "Verificación"
    },
    
    // Landing Page
    landing: {
      hero_title: "Donde los Profesionales de Alto Patrimonio se Conectan y Cultivan Relaciones",
      hero_subtitle: "Bienvenido al portal privado de membresía de EchoSense Capital Club: donde las relaciones crean acceso y el acceso crea oportunidad. Complete su perfil y verificación para ingresar a una comunidad privada construida en torno a la colaboración, la educación, socios seleccionados y oportunidades normalmente reservadas para instituciones.",
      cta_primary: "Comenzar",
      cta_secondary: "Acerca del Capital Club",
      invite_badge: "Acceso Exclusivo a la Red"
    },
    
    // About Page - Compliance Focused
    about: {
      title: "Sobre EchoSense Capital Club",
      subtitle: "Plataforma de Gestión de Introductores y Seguimiento de Cumplimiento",
      
      section1_title: "¿Qué es EchoSense Capital Club?",
      section1_content: "EchoSense Capital Club opera como una plataforma de gestión de introductores y seguimiento de cumplimiento, distinta de las operaciones de inversión de EchoSense Capital Club. Está diseñada para rastrear referencias, progreso de incorporación y puntos — no para vender nada ni dar asesoramiento de inversión.",
      
      section2_title: "Lo que EchoSense Capital Club NO HACE!",
      section2_items: [
        "Ofrecer inversiones",
        "Recomendar valores",
        "Aceptar capital",
        "Proporcionar asesoramiento de inversión"
      ],
      section2_note: "Los introductores no discuten términos, rendimientos o estructuras.",
      
      section3_title: "¿Por qué esta estructura?",
      section3_content: "La actividad se limita a introducciones y visibilidad de estado. Sin compensación basada en transacciones vinculada a ventas de valores. Sin discreción, persuasión o solicitud. El sistema de puntos rastrea acciones administrativas, no colocación de capital.",
      
      section4_title: "KYC y Cumplimiento",
      section4_content: "El KYC a nivel de introductor se realiza preventivamente para evitar retrasos de cumplimiento posteriores al financiamiento y satisfacer los controles AML y de pago. El KYC es separado de la incorporación de inversores.",
      
      section5_title: "Separación Estructural",
      section5_content: "EchoSense Capital Club y EchoSense Capital Club son entidades separadas. EchoSense Capital Club no intermedia transacciones. EchoSense Capital Club maneja independientemente toda la idoneidad del inversor, incorporación y financiamiento.",
      
      conclusion_title: "Declaración de Cumplimiento",
      conclusion_content: "EchoSense Capital Club funciona como un sistema de gestión de referencias no transaccional, no como corredor, distribuidor o buscador bajo las leyes de valores de EE.UU."
    },
    
    // FAQ Page
    faq: {
      title: "Preguntas Frecuentes",
      subtitle: "Todo lo que necesitas saber sobre EchoSense Capital Club",
      
      questions: [
        {
          q: "¿Qué es EchoSense Capital Club?",
          a: "EchoSense Capital Club es el portal backend para rastrear referencias conectadas a EchoSense Capital Club. Sin inversiones, sin ventas. Solo una forma limpia de ver a quién has presentado, dónde están en la incorporación/financiamiento, y manejar el cumplimiento temprano para que nada se retrase después."
        },
        {
          q: "¿Es EchoSense Capital Club una plataforma de inversión?",
          a: "No. EchoSense Capital Club es estrictamente para seguimiento de introductores y cumplimiento. No ofrece inversiones, no recomienda valores, no acepta capital, ni proporciona asesoramiento de inversión. Opera completamente separado de las operaciones de inversión de EchoSense Capital Club."
        },
        {
          q: "¿Qué puedo ver en mi panel?",
          a: "Puedes ver: A quién presentaste, dónde están en la incorporación o financiamiento, a quién presentaron ellos. Sin saldos. Sin detalles de inversión. Solo estado y progreso."
        },
        {
          q: "¿Por qué necesito completar KYC?",
          a: "Esto ayuda a evitar retrasos más adelante a medida que tu actividad de referencia progresa. Hacerlo ahora mantiene las cosas fluidas si los fondos se mueven después. El KYC se realiza preventivamente para satisfacer los controles AML y de pago."
        },
        {
          q: "¿Cómo funciona el sistema de puntos?",
          a: "Los puntos se actualizan automáticamente a medida que: Completas pasos de configuración, tus referencias avanzan en la incorporación, las introducciones de segundo nivel progresan. Los puntos son para seguimiento y transparencia, no rendimiento."
        },
        {
          q: "¿Es esto actividad de corredor-distribuidor?",
          a: "No. La actividad se limita a introducciones y visibilidad de estado. No hay compensación basada en transacciones vinculada a ventas de valores, sin discreción, persuasión o solicitud. El sistema de puntos rastrea acciones administrativas, no colocación de capital."
        },
        {
          q: "¿Cuál es la relación entre EchoSense Capital Club y EchoSenseAi / FinBPM?",
          a: "Son entidades separadas con propiedad y marco jurisdiccional separados."
        },
        {
          q: "¿Puedo discutir términos de inversión con mis referencias?",
          a: "No. Los introductores no discuten términos, rendimientos o estructuras. EchoSense Capital Club mantiene todo limpio para que nunca parezcas estar vendiendo o promoviendo un valor."
        }
      ]
    },
    
    // Onboarding Steps
    onboarding: {
      step1_title: "Crea tu Cuenta de EchoSense Capital Club",
      step1_desc: "Configura tu inicio de sesión para que sepamos quién eres en el sistema. Esto es solo para seguimiento y cumplimiento — no inversión.",
      
      step2_title: "Completa el KYC de Introductor (Temprano)",
      step2_desc: "Esto ayuda a evitar retrasos más adelante a medida que tu actividad de referencia progresa. Hacerlo ahora mantiene las cosas fluidas si los fondos se mueven después.",
      
      step3_title: "Ve tu Panel de Referencias",
      step3_desc: "Ve a quién presentaste, dónde están en la incorporación o financiamiento, y a quién presentaron ellos. Sin saldos. Sin detalles de inversión. Solo estado y progreso.",
      
      step4_title: "Los Puntos Siguen Tu Actividad",
      step4_desc: "Los puntos se actualizan automáticamente a medida que completas pasos de configuración, tus referencias avanzan en la incorporación, y las introducciones de segundo nivel progresan. Los puntos son para seguimiento y transparencia, no rendimiento.",
      
      step5_title: "Mantente Cumplido, Mantente Visible",
      step5_desc: "EchoSense Capital Club mantiene todo limpio para que nunca parezcas estar vendiendo o promoviendo un valor."
    },
    
    // Invite Messages
    invite: {
      short: "Estás invitado a EchoSense Capital Club — el portal backend para rastrear referencias conectadas a EchoSense Capital Club. Sin inversiones, sin ventas. Solo una forma limpia de ver a quién has presentado.",
      long: "Has sido invitado a EchoSense Capital Club — el portal de introductores que está junto a EchoSense Capital Club (no dentro de él). Está diseñado para rastrear referencias, progreso de incorporación y puntos — no para vender nada ni dar asesoramiento de inversión. Configura tu cuenta, completa el KYC temprano, y obtén visibilidad de tu red sin dolores de cabeza después."
    },
    
    // Common
    common: {
      activate_here: "Activar aquí",
      learn_more: "Más Información",
      get_started: "Comenzar",
      contact_us: "Contáctanos",
      back: "Atrás",
      next: "Siguiente",
      submit: "Enviar",
      cancel: "Cancelar",
      save: "Guardar",
      loading: "Cargando...",
      error: "Error",
      success: "Éxito"
    }
  }
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Check localStorage first
    const saved = localStorage.getItem('echosense_language');
    if (saved && translations[saved]) return saved;
    
    // Check browser language
    const browserLang = navigator.language?.split('-')[0];
    if (browserLang === 'es') return 'es';
    
    return 'en';
  });

  useEffect(() => {
    localStorage.setItem('echosense_language', language);
    document.documentElement.lang = language;
  }, [language]);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      value = value?.[k];
      if (value === undefined) {
        // Fallback to English
        value = translations.en;
        for (const fk of keys) {
          value = value?.[fk];
          if (value === undefined) return key;
        }
        return value;
      }
    }
    
    return value;
  };

  const toggleLanguage = () => {
    setLanguage(lang => lang === 'en' ? 'es' : 'en');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t, translations: translations[language] }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
